import React, { useMemo } from "react";
import styled from "@emotion/styled";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import formatter from "../../utils/formatters/formatter";
import { css } from "../../utils/ide";
import { noop } from "lodash-es";

const TotalRows = styled.span`
  font-size: 12px;
  margin-right: 50px;
`;

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${({ position }) => position};
  margin: 20px 10px;
  color: ${({ theme }) => theme.text.secondary};

  & div {
    border: 1px solid #666;
    padding: 5px 10px;

    &:not([disabled]) {
      cursor: pointer;

      &:hover {
        background-color: ${({ theme }) => theme.background.secondary};
      }
    }
  }

  & div:first-of-type {
    border-radius: 5px 0 0 5px;
  }

  & div:last-child {
    border-radius: 0 5px 5px 0;
  }
`;

const CurrentPage = styled.div`
  cursor: default;
  padding: 5px 10px;
  background-color: ${({ theme }) => theme.background.secondary};
`;

const PageItem = styled.div(
  ({ disabled }) => css`
    pointer-events: ${disabled ? "none" : "auto"};
    opacity: ${disabled ? 0.7 : 1};
    font-weight: bold;
  `
);

export default function Pagination({
  currentPage,
  showTotalRows = true,
  totalRows,
  lastPage,
  changePage,
  first = currentPage === 1 ? null : () => changePage(1),
  last = currentPage === lastPage ? null : () => changePage(lastPage),
  next = currentPage === lastPage ? null : () => changePage(currentPage + 1),
  previous = currentPage === 1 ? null : () => changePage(currentPage - 1),
  position = "flex-end",
  maxNeighboringPages = 4,
}) {
  const neighboringPages = useMemo(() => {
    const ret = [];

    if (!lastPage || !changePage) {
      return ret;
    }

    for (const isNext of [false, true]) {
      const neighboringPagesOfDirection = [];
      for (let i = 0; i < maxNeighboringPages; i++) {
        const neighboringPage = isNext
          ? currentPage + i + 1
          : currentPage - maxNeighboringPages + i;
        if (0 < neighboringPage && neighboringPage <= lastPage) {
          neighboringPagesOfDirection.push(neighboringPage);
        }
      }
      ret.push(neighboringPagesOfDirection);
    }

    return ret;
  }, [changePage, currentPage, lastPage, maxNeighboringPages]);

  if (!totalRows) return null;

  return (
    <PaginationContainer data-cy="pagination-container" position={position}>
      {showTotalRows && (
        <span style={{ display: "inline-grid" }}>
          <TotalRows>Total Rows: {formatter(totalRows, "number")}</TotalRows>
          <TotalRows>Total Pages: {formatter(lastPage, "number")}</TotalRows>
        </span>
      )}
      <PageItem onClick={first} disabled={!first} data-cy="pagination-first">
        <FontAwesomeIcon
          style={{ fontSize: "1em" }}
          icon={["fas", "angle-double-left"]}
        />
      </PageItem>
      <PageItem onClick={previous} disabled={!previous}>
        <FontAwesomeIcon
          style={{ fontSize: "1em" }}
          icon={["fas", "angle-left"]}
        />
      </PageItem>
      {neighboringPages[0]?.map((page) => (
        <PageItem key={page} onClick={() => changePage(page)}>
          {page}
        </PageItem>
      ))}
      <CurrentPage className="current-page" disabled>
        {currentPage}
      </CurrentPage>
      {neighboringPages[1]?.map((page) => (
        <PageItem key={page} onClick={() => changePage(page)}>
          {page}
        </PageItem>
      ))}
      <PageItem onClick={next} disabled={!next}>
        <FontAwesomeIcon
          style={{ fontSize: "1em" }}
          icon={["fas", "angle-right"]}
        />
      </PageItem>
      <PageItem onClick={last} disabled={!last}>
        <FontAwesomeIcon
          style={{ fontSize: "1em" }}
          icon={["fas", "angle-double-right"]}
        />
      </PageItem>
    </PaginationContainer>
  );
}

export function convertMetaPaginationToProps(meta) {
  return {
    currentPage: meta.current_page,
    showTotalRows: true,
    totalRows: meta.total,
    lastPage: meta.last_page,
    changePage: noop,
  };
}
